import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import CryptoJS from 'crypto-js';
import { useTranslation } from 'react-i18next';

import PageContainer from '@/components/Container/PageContainer';
import RegistrationStepLine from '@/components/RegistrationStepLine';
import { Button } from '@/components/UI/Button';
import { Flex } from '@/layout';
import { pblServiceAreas } from '@/data/pbl';
import {
  ActionRow,
  initialVolunteerForm,
  volunteerStepItems,
  volunteerSteps,
  type VolunteerFormState,
  type UpdateVolunteerForm,
  PersonalInformationStep,
  PracticeInformationStep,
  ServiceAreasStep,
  ProfileCvStep,
  AuthorisationStep,
  ServicePledgeStep,
  ConductCheckStep,
  SubmittedStep,
} from '@/components/Pbl/VolunteerRegistration';
import PostCompletionStep from '@/components/Pbl/VolunteerRegistration/steps/PostCompletionStep';
import PersonalInfoCollectionStep from '@/components/Pbl/VolunteerRegistration/steps/PersonalInfoCollectionStep';
import DeclarationStep from '@/components/Pbl/VolunteerRegistration/steps/DeclarationStep';
import ReviewSubmitStep from '@/components/Pbl/VolunteerRegistration/steps/ReviewSubmitStep';
import {
  buildVolunteerFullNameEn,
  buildVolunteerFullNameZh,
  buildVolunteerApplicationFormData,
  mapPblLawyerProfileToVolunteerForm,
  requiresConductSupportingDocument,
  requiresOtherProfessionalQualificationText,
} from '@/components/Pbl/VolunteerRegistration/formState';
import { getFirstVolunteerValidationIssue } from '@/components/Pbl/VolunteerRegistration/validation';
import { getMyPblLawyerApplication, submitPblLawyerApplication } from '@/api/pbl.api';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import useResize from '@/hooks/useResize';

const VOLUNTEER_REGISTRATION_DRAFT_KEY = 'pblVolunteerRegistrationDraft';

const sanitizeUploadedDocument = (document: VolunteerFormState['firm_authorisation_file']) => {
  if (!document) return document;

  return {
    file_id: document.file_id,
    name: document.name,
    url: document.url,
  };
};

const clearTransientVerificationState = (form: VolunteerFormState): VolunteerFormState => ({
  ...form,
  phone_otp: '',
  phone_otp_verified: false,
  email_otp: '',
  email_otp_verified: false,
  email_verification_token: '',
  email_verification_code: '',
});

const buildVolunteerRegistrationDraft = (form: VolunteerFormState): VolunteerFormState => ({
  ...clearTransientVerificationState(form),
  firm_authorisation_file: sanitizeUploadedDocument(form.firm_authorisation_file),
  conduct_supporting_document_file: sanitizeUploadedDocument(form.conduct_supporting_document_file),
});

const loadVolunteerRegistrationDraft = () => {
  try {
    const storedDraft = localStorage.getItem(VOLUNTEER_REGISTRATION_DRAFT_KEY);
    if (!storedDraft) return initialVolunteerForm;

    const parsedDraft = JSON.parse(storedDraft) as Partial<VolunteerFormState>;
    if (!parsedDraft || typeof parsedDraft !== 'object') return initialVolunteerForm;

    return clearTransientVerificationState({
      ...initialVolunteerForm,
      ...parsedDraft,
      firm_authorisation_file: sanitizeUploadedDocument(parsedDraft.firm_authorisation_file || null),
      conduct_supporting_document_file: sanitizeUploadedDocument(parsedDraft.conduct_supporting_document_file || null),
    });
  } catch {
    return initialVolunteerForm;
  }
};

const saveVolunteerRegistrationDraft = (form: VolunteerFormState) => {
  localStorage.setItem(VOLUNTEER_REGISTRATION_DRAFT_KEY, JSON.stringify(buildVolunteerRegistrationDraft(form)));
};

const clearVolunteerRegistrationDraft = () => {
  localStorage.removeItem(VOLUNTEER_REGISTRATION_DRAFT_KEY);
};

const VolunteerRegistration = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const tr = useCallback((key: string, defaultValue: string) => t(key as any, { defaultValue }) as string, [t]);
  const { isMobile, isTablet } = useResize();
  const [searchParams, setSearchParams] = useSearchParams();
  const isResubmission = searchParams.get('mode') === 'resubmit';
  const defaultFormValues = useMemo(
    () => (isResubmission ? initialVolunteerForm : loadVolunteerRegistrationDraft()),
    [isResubmission]
  );
  const parsedStep = Number(searchParams.get('step') || '1') - 1;
  const initialStep = Number.isNaN(parsedStep) ? 0 : Math.min(Math.max(0, parsedStep), volunteerSteps.length - 1);
  const [activeStep, setActiveStep] = useState(initialStep);
  const [submitted, setSubmitted] = useState(searchParams.get('submitted') === '1');
  const [submittedReferenceId, setSubmittedReferenceId] = useState(searchParams.get('reference') || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [pendingFocusField, setPendingFocusField] = useState<keyof VolunteerFormState | null>(null);
  const {
    control,
    watch,
    trigger,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<VolunteerFormState>({
    mode: 'onBlur',
    defaultValues: defaultFormValues,
  });
  const form = watch();

  const currentTitle = tr('pbl.lawyerRegistration.title', 'Create Account - FreeLegalLink Panel of Pro Bono Lawyers');
  const submittedTitle = tr(
    'pbl.lawyerRegistration.submittedTitle',
    'Panel of Pro Bono Lawyers of FreeLegalLink Registration'
  );
  const translatedVolunteerSteps = useMemo(
    () => volunteerStepItems.map((item) => tr(item.key, item.defaultLabel)),
    [tr]
  );
  const isLastStep = activeStep === volunteerSteps.length - 1;
  const requiresAccountVerification = !isResubmission;

  const isCurrentStepComplete = useMemo(() => {
    const hasText = (value?: string) => Boolean(value?.trim());
    const hasFirmAuthorisationFile = typeof form.firm_authorisation_file_id === 'number';
    const hasConductSupportingDocument = typeof form.conduct_supporting_document_file_id === 'number';

    if (activeStep === 0) {
      return (
        hasText(form.surname_en) &&
        hasText(form.given_name_en) &&
        hasText(form.surname_zh) &&
        hasText(form.given_name_zh) &&
        (!requiresAccountVerification || hasText(form.phone)) &&
        (!requiresAccountVerification || form.phone_otp_verified) &&
        (!requiresAccountVerification || hasText(form.email)) &&
        (!requiresAccountVerification || form.email_otp_verified) &&
        hasText(form.hk_solicitor_admission_year) &&
        (!requiresAccountVerification || hasText(form.password)) &&
        (!requiresAccountVerification || hasText(form.password_confirmation)) &&
        !!form.other_professional_qualifications?.length &&
        (!requiresOtherProfessionalQualificationText(form) || hasText(form.other_professional_qualification_text)) &&
        !!form.language_proficiency?.length
      );
    }

    if (activeStep === 1) {
      return hasText(form.law_firm_name) && hasText(form.position_in_law_firm);
    }

    if (activeStep === 2) {
      return !!form.service_areas?.length;
    }

    if (activeStep === 3) {
      return (
        form.cv_publication_consent &&
        hasText(form.cv_primary_practice_areas_en) &&
        hasText(form.cv_primary_practice_areas_zh)
      );
    }

    if (activeStep === 4) {
      return (
        hasFirmAuthorisationFile &&
        form.firm_authorisation_confirmed &&
        form.firm_authorisation_letter_attached &&
        form.law_firm_change_notify_agreed
      );
    }

    if (activeStep === 5) {
      return form.service_pledge_agreed;
    }

    if (activeStep === 6) {
      return form.post_completion_agreed;
    }

    if (activeStep === 7) {
      return form.pics_agreed;
    }

    if (activeStep === 8) {
      return (
        form.bankrupt_or_insolvent !== undefined &&
        form.arrangement_with_creditors !== undefined &&
        form.criminal_conviction !== undefined &&
        form.professional_misconduct_or_pending_disciplinary !== undefined &&
        form.suspended_from_professional_practice !== undefined &&
        (!requiresConductSupportingDocument(form) || hasConductSupportingDocument)
      );
    }

    if (activeStep === 9) {
      return hasText(form.declaration_signature) && hasText(form.declaration_signed_at);
    }

    if (activeStep === volunteerSteps.length - 1) {
      return true;
    }

    return true;
  }, [activeStep, form, requiresAccountVerification]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    const nextStep = String(activeStep + 1);

    if (nextParams.get('step') === nextStep) return;

    nextParams.set('step', nextStep);
    setSearchParams(nextParams, { replace: true });
  }, [activeStep, searchParams, setSearchParams]);

  useEffect(() => {
    if (!pendingFocusField) return;

    const timeoutId = window.setTimeout(() => {
      const stepContainer = document.querySelector<HTMLElement>(`[data-volunteer-step="${activeStep}"]`);
      if (!stepContainer) return;

      const fieldName = String(pendingFocusField);
      const namedField = Array.from(stepContainer.querySelectorAll<HTMLElement>(`[name="${fieldName}"]`)).find(
        (element) => !element.hasAttribute('disabled')
      );
      const markedField = stepContainer.querySelector<HTMLElement>(`[data-volunteer-field="${fieldName}"]`);
      const markedFocusable = markedField?.matches('input, textarea, select, button, [tabindex]')
        ? markedField
        : markedField?.querySelector<HTMLElement>(
            'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]'
          );
      const focusTarget =
        namedField ||
        markedFocusable ||
        stepContainer.querySelector<HTMLElement>('[aria-invalid="true"]') ||
        stepContainer.querySelector<HTMLElement>(
          'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]'
        );

      focusTarget?.focus();
      if (focusTarget && typeof focusTarget.scrollIntoView === 'function') {
        focusTarget.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      setPendingFocusField(null);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [activeStep, pendingFocusField]);

  useEffect(() => {
    if (!isResubmission) return;

    let isMounted = true;

    const loadExistingApplication = async () => {
      try {
        const response = await getMyPblLawyerApplication();

        if (!isMounted || response.data.success === false || !response.data.data) return;

        reset(mapPblLawyerProfileToVolunteerForm(response.data.data));
        clearVolunteerRegistrationDraft();
      } catch (error: any) {
        if (!isMounted) return;

        errorHandler(
          error.response?.data?.message ||
            error.response?.data?.data ||
            error.message ||
            tr(
              'pbl.lawyerRegistration.messages.loadResubmissionFailed',
              t('pbl.pagesHardcoded.volunteerRegistration.failedToLoadApplicationForReSubmission')
            )
        );
      }
    };

    loadExistingApplication();

    return () => {
      isMounted = false;
    };
  }, [isResubmission, reset, tr]);

  const updateForm: UpdateVolunteerForm = (key, value) => {
    setValidationError('');
    clearErrors(key);
    setValue(key as any, value as any, { shouldDirty: true, shouldValidate: true });

    if (['surname_en', 'given_name_en', 'surname_zh', 'given_name_zh'].includes(key as string)) {
      const nextForm = {
        ...form,
        [key]: value,
      };
      setValue('full_name_en', buildVolunteerFullNameEn(nextForm), { shouldDirty: true });
      setValue('full_name_zh', buildVolunteerFullNameZh(nextForm), { shouldDirty: true });
    }
  };

  const serviceAreaLabels = useMemo(
    () =>
      form.service_areas.map((area) => {
        const serviceArea = pblServiceAreas.find((item) => item.value === area);
        return serviceArea ? tr(serviceArea.labelKey, serviceArea.defaultLabel) : area;
      }),
    [form.service_areas, tr]
  );

  const submitApplication = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const submitForm = {
        ...form,
        review_confirmed: true,
        password: form.password ? CryptoJS.MD5(form.password).toString() : '',
        password_confirmation: form.password_confirmation ? CryptoJS.MD5(form.password_confirmation).toString() : '',
      };
      const includePassword = Boolean(form.password?.trim() || form.password_confirmation?.trim());
      const response = await submitPblLawyerApplication(
        buildVolunteerApplicationFormData(submitForm, {
          includePassword,
          includeAccountFields: !isResubmission,
        })
      );

      if (response.data.success === false) {
        errorHandler(
          response.data.message ||
            t('pbl.lawyerRegistration.messages.submitFailed', {
              defaultValue: 'Failed to submit application.',
            })
        );
        return;
      }

      const submittedProfile = response.data.data;
      const nextReferenceId =
        submittedProfile.reference_id ||
        (submittedProfile as { application_reference?: string }).application_reference ||
        '';

      setSubmittedReferenceId(nextReferenceId);
      setSubmitted(true);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('submitted', '1');
      if (nextReferenceId) nextParams.set('reference', nextReferenceId);
      setSearchParams(nextParams, { replace: true });
      clearVolunteerRegistrationDraft();
      successHandler(
        response.data.message ||
          t('pbl.lawyerRegistration.messages.applicationSubmitted', {
            defaultValue: 'Application submitted',
          })
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      const emailVerificationErrors = error.response?.data?.errors?.email_verification_code;
      if (Array.isArray(emailVerificationErrors) && emailVerificationErrors.length > 0) {
        setValue('email_otp', '', { shouldDirty: true, shouldValidate: true });
        setValue('email_otp_verified', false, { shouldDirty: true, shouldValidate: true });
        setValue('email_verification_token', '', { shouldDirty: true, shouldValidate: true });
        setValue('email_verification_code', '', { shouldDirty: true, shouldValidate: true });
        setActiveStep(0);
        setValidationError(
          tr(
            'pbl.lawyerRegistration.validation.emailOtpInvalidRetry',
            'Email OTP is invalid or expired. Please request and verify a new Email OTP.'
          )
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      errorHandler(
        error.response?.data?.message ||
          error.response?.data?.data ||
          error.message ||
          t('pbl.lawyerRegistration.messages.submitFailed', {
            defaultValue: 'Failed to submit application.',
          })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateCompleteApplication = () => {
    const validationIssue = getFirstVolunteerValidationIssue(form, requiresAccountVerification);
    if (!validationIssue) return true;

    const validationMessage = t(validationIssue.messageKey as any) as string;

    setError(validationIssue.field, {
      type: 'manual',
      message: validationMessage,
    });
    setValidationError(validationMessage);
    setPendingFocusField(validationIssue.field);
    setActiveStep(validationIssue.stepIndex);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return false;
  };

  const validateCurrentStep = async () => {
    setValidationError('');

    if (activeStep === 0) {
      const personalInfoFields: Array<keyof VolunteerFormState> = [
        'surname_en',
        'given_name_en',
        'surname_zh',
        'given_name_zh',
        'hk_solicitor_admission_year',
      ];

      if (requiresAccountVerification) {
        personalInfoFields.push('phone', 'email', 'password', 'password_confirmation');
      }

      const isValid = await trigger(personalInfoFields);

      let hasManualError = false;

      if (!form.other_professional_qualifications?.length) {
        setError('other_professional_qualifications', {
          type: 'manual',
          message: t('pbl.lawyerRegistration.validation.selectProfessionalQualification', {
            defaultValue: 'Please select at least one professional qualification.',
          }),
        });
        hasManualError = true;
      }

      if (requiresOtherProfessionalQualificationText(form) && !form.other_professional_qualification_text?.trim()) {
        setError('other_professional_qualification_text', {
          type: 'manual',
          message: t('pbl.lawyerRegistration.validation.specifyProfessionalQualification', {
            defaultValue: 'Please specify your other professional qualification.',
          }),
        });
        hasManualError = true;
      } else {
        clearErrors('other_professional_qualification_text');
      }

      if (!form.language_proficiency?.length) {
        setError('language_proficiency', {
          type: 'manual',
          message: t('pbl.lawyerRegistration.validation.selectLanguage', {
            defaultValue: 'Please select at least one language.',
          }),
        });
        hasManualError = true;
      }

      const missingOtpVerification =
        requiresAccountVerification && (!form.phone_otp_verified || !form.email_otp_verified);

      if (missingOtpVerification) {
        setValidationError(
          t('pbl.lawyerRegistration.validation.verifySmsAndEmailOtp', {
            defaultValue: 'Please verify both SMS and Email OTP before proceeding.',
          })
        );
        hasManualError = true;
      }

      if (!isValid || hasManualError) {
        setValidationError(
          missingOtpVerification
            ? t('pbl.lawyerRegistration.validation.verifySmsAndEmailOtp', {
                defaultValue: 'Please verify both SMS and Email OTP before proceeding.',
              })
            : t('pbl.lawyerRegistration.validation.completePersonalInformation', {
                defaultValue: 'Please complete all required personal information fields.',
              })
        );
      }

      return isValid && !hasManualError;
    }

    if (activeStep === 1) {
      return trigger(['law_firm_name', 'position_in_law_firm']);
    }

    if (activeStep === 2) {
      if (!form.service_areas?.length) {
        setError('service_areas', {
          type: 'manual',
          message: t('pbl.lawyerRegistration.validation.selectServiceArea', {
            defaultValue: 'Please select at least one service area.',
          }),
        });
        return false;
      }

      clearErrors('service_areas');
      return true;
    }

    if (activeStep === 3) {
      const isValid = await trigger([
        'cv_primary_practice_areas_en',
        'cv_primary_practice_areas_zh',
        'cv_professional_experience_en',
        'cv_professional_experience_zh',
        'cv_pro_bono_experience_en',
        'cv_pro_bono_experience_zh',
      ]);

      let hasManualError = false;

      if (!form.cv_publication_consent) {
        setError('cv_publication_consent', {
          type: 'manual',
          message: t('pbl.lawyerRegistration.profileCv.errors.consentBeforeProceeding', {
            defaultValue: 'Please consent to CV publication before proceeding.',
          }),
        });
        hasManualError = true;
      } else {
        clearErrors('cv_publication_consent');
      }

      if (!isValid || hasManualError) {
        setValidationError(
          t('pbl.lawyerRegistration.validation.completeProfileCv', {
            defaultValue: 'Please complete Profile & CV information and consent.',
          })
        );
      }

      return isValid && !hasManualError;
    }

    if (activeStep === 4) {
      const isValid = await trigger([
        'firm_authorisation_confirmed',
        'firm_authorisation_letter_attached',
        'law_firm_change_notify_agreed',
      ]);
      const missingFirmAuthorisationFile = typeof form.firm_authorisation_file_id !== 'number';

      if (missingFirmAuthorisationFile) {
        setError('firm_authorisation_file_id', {
          type: 'manual',
          message: t('pbl.lawyerRegistration.authorisation.errors.uploadRequired', {
            defaultValue: 'Please upload the signed authorisation form PDF.',
          }),
        });
      } else {
        clearErrors('firm_authorisation_file_id');
      }

      if (!isValid || missingFirmAuthorisationFile) {
        setValidationError(
          t('pbl.lawyerRegistration.validation.uploadAndConfirmAuthorisation', {
            defaultValue: 'Please upload and confirm law firm authorisation before proceeding.',
          })
        );
      }

      return isValid && !missingFirmAuthorisationFile;
    }

    if (activeStep === 5) {
      const isValid = await trigger(['service_pledge_agreed']);

      if (!isValid) {
        setValidationError(
          t('pbl.lawyerRegistration.validation.agreeServicePledge', {
            defaultValue: 'Please agree to the service pledge before proceeding.',
          })
        );
      }

      return isValid;
    }

    if (activeStep === 6) {
      const isValid = await trigger(['post_completion_agreed']);

      if (!isValid) {
        setValidationError(
          t('pbl.lawyerRegistration.validation.agreePostCompletion', {
            defaultValue: 'Please agree to post-completion requirements before proceeding.',
          })
        );
      }

      return isValid;
    }

    if (activeStep === 7) {
      const isValid = await trigger(['pics_agreed']);

      if (!isValid) {
        setValidationError(
          t('pbl.lawyerRegistration.validation.acknowledgePics', {
            defaultValue: 'Please acknowledge the personal information collection statement before proceeding.',
          })
        );
      }

      return isValid;
    }

    if (activeStep === 8) {
      const isValid = await trigger([
        'bankrupt_or_insolvent',
        'arrangement_with_creditors',
        'criminal_conviction',
        'professional_misconduct_or_pending_disciplinary',
        'suspended_from_professional_practice',
      ]);

      const hasConductSupportingDocument = typeof form.conduct_supporting_document_file_id === 'number';
      const missingSupportingDocument = requiresConductSupportingDocument(form) && !hasConductSupportingDocument;

      if (missingSupportingDocument) {
        setError('conduct_supporting_document_file_id', {
          type: 'manual',
          message: t('pbl.lawyerRegistration.validation.uploadConductSupportingDocument', {
            defaultValue: 'Please upload supporting documents for any Yes conduct check answer.',
          }),
        });
      } else {
        clearErrors('conduct_supporting_document_file_id');
      }

      if (!isValid || missingSupportingDocument) {
        setValidationError(
          t('pbl.lawyerRegistration.validation.answerConductCheck', {
            defaultValue: 'Please answer all conduct check questions before proceeding.',
          })
        );
      }

      return isValid && !missingSupportingDocument;
    }

    if (activeStep === 9) {
      const isValid = await trigger(['declaration_signature', 'declaration_signed_at']);

      if (!isValid) {
        setValidationError(
          t('pbl.lawyerRegistration.validation.completeSignature', {
            defaultValue: 'Please complete signature and date before proceeding.',
          })
        );
      }

      return isValid;
    }

    return true;
  };

  const next = async () => {
    if (isLastStep) {
      if (!validateCompleteApplication()) return;
      await submitApplication();
      return;
    }

    if (!(await validateCurrentStep())) return;

    setActiveStep((step) => step + 1);
  };

  const back = () => {
    setActiveStep((step) => Math.max(0, step - 1));
  };

  const jumpToStep = (stepIndex: number) => {
    setActiveStep(Math.min(Math.max(0, stepIndex), volunteerSteps.length - 1));
  };

  const saveDraft = () => {
    try {
      saveVolunteerRegistrationDraft(form);
      successHandler(t('pbl.lawyerRegistration.messages.draftSaved', { defaultValue: 'Draft saved locally' }));
    } catch {
      errorHandler(
        t('pbl.lawyerRegistration.messages.draftSaveFailed', {
          defaultValue: 'Failed to save draft locally. Please try again.',
        })
      );
    }
  };

  const renderStep = () => {
    if (submitted) {
      return <SubmittedStep isMobile={isMobile} isTablet={isTablet} referenceId={submittedReferenceId} />;
    }

    if (activeStep === 0) {
      return (
        <PersonalInformationStep
          form={form}
          validationError={validationError}
          errors={errors}
          control={control}
          isResubmission={isResubmission}
          isMobile={isMobile}
          isTablet={isTablet}
          updateForm={updateForm}
        />
      );
    }

    if (activeStep === 1) {
      return (
        <PracticeInformationStep
          form={form}
          errors={errors}
          control={control}
          isMobile={isMobile}
          isTablet={isTablet}
          updateForm={updateForm}
        />
      );
    }

    if (activeStep === 2) {
      return <ServiceAreasStep form={form} errors={errors} isTablet={isTablet} updateForm={updateForm} />;
    }

    if (activeStep === 3) {
      return (
        <ProfileCvStep
          form={form}
          errors={errors}
          control={control}
          isMobile={isMobile}
          isTablet={isTablet}
          updateForm={updateForm}
        />
      );
    }

    if (activeStep === 4) {
      return (
        <AuthorisationStep form={form} errors={errors} control={control} isTablet={isTablet} updateForm={updateForm} />
      );
    }

    if (activeStep === 5) {
      return (
        <ServicePledgeStep form={form} errors={errors} control={control} isTablet={isTablet} updateForm={updateForm} />
      );
    }

    if (activeStep === 6) {
      return <PostCompletionStep form={form} errors={errors} control={control} isTablet={isTablet} />;
    }

    if (activeStep === 7) {
      return <PersonalInfoCollectionStep form={form} errors={errors} control={control} isTablet={isTablet} />;
    }

    if (activeStep === 8) {
      return (
        <ConductCheckStep form={form} errors={errors} control={control} isTablet={isTablet} updateForm={updateForm} />
      );
    }

    if (activeStep === 9) {
      return (
        <DeclarationStep form={form} errors={errors} control={control} isTablet={isTablet} updateForm={updateForm} />
      );
    }

    return (
      <ReviewSubmitStep form={form} isTablet={isTablet} serviceAreaLabels={serviceAreaLabels} onEditStep={jumpToStep} />
    );
  };

  return (
    <PageContainer title={submitted ? submittedTitle : currentTitle}>
      <Flex flexDirection={isTablet ? 'column' : 'row'} alignItems="flex-start" gridGap={isTablet ? 0 : 30}>
        {!submitted && (
          <RegistrationStepLine activeStep={activeStep} stepsLine={translatedVolunteerSteps} onStepClick={jumpToStep} />
        )}
        <div style={{ width: '100%' }}>
          <div data-volunteer-step={activeStep}>{renderStep()}</div>
          {!submitted && (
            <ActionRow isMobile={isMobile}>
              <Button
                size="lg"
                variant="primary"
                rounded={false}
                onClick={next}
                disabled={!isCurrentStepComplete || isSubmitting}
              >
                {isLastStep
                  ? isSubmitting
                    ? t('pbl.common.actions.submitting', { defaultValue: 'Submitting...' })
                    : t('pbl.common.actions.submitApplication', { defaultValue: 'Submit Application' })
                  : t('pbl.common.actions.next', { defaultValue: 'Next' })}
              </Button>
              {activeStep > 0 && (
                <Button size="lg" variant="primary-invert" rounded={false} onClick={back}>
                  {t('pbl.common.actions.back', { defaultValue: 'Back' })}
                </Button>
              )}
              <Button size="lg" variant="primary" rounded={false} type="button" onClick={saveDraft}>
                {t('pbl.common.actions.save', { defaultValue: 'Save' })}
              </Button>
            </ActionRow>
          )}
        </div>
      </Flex>
    </PageContainer>
  );
};

export default VolunteerRegistration;
